"use client";

import Link from "next/link";
import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { useTranslations } from "next-intl";
import { useReCaptcha } from "next-recaptcha-v3";
import { getLink, Routes } from "@/constants";
import { AppContext } from "@/contextApi/AppProvider";
import { AppContextType } from "@/interFace/general";
import { TUTORING_CENTRES_NL_SEARCH_V2_ENABLED } from "@/config/general/public-flags";
import { trackEducationearchEvent } from "@/lib/analytics/tutoringCentreSearch";
import { parseNaturalEducationSearchQuery } from "@/lib/search/educationNaturalQuery";
import { Translations } from "@/translations";
import { getEducationSearchRecaptchaToken } from "../getEducationSearchRecaptchaToken";
import { useEducationAvailability } from "@/hooks/useEducationAvailability";
import { normalizeCountryCode } from "@/lib/location/visitorCountry";
import AvailabilityNotice from "../discovery/AvailabilityNotice";

type Centre = {
  id: string;
  slug?: string;
  name?: string;
  businessGroupId?: string;
  businessGroupName?: string;
  campusName?: string;
  isPrimaryCampus?: boolean;
  shortDescription?: string;
  phone?: string;
  showPhonePublicly?: boolean;
  businessTypeSlug?: string;
  businessSubtypeSlug?: string;
  localityName?: string;
  adminLevel2Name?: string;
  adminLevel1Name?: string;
  city?: string;
  municipality?: string;
  countryCode?: string;
  updatedDate?: string;
};

type SearchResponse = {
  centres?: Centre[];
  resolvedPlace?: {
    id?: string;
    level?: string;
    name?: string;
    displayName?: string;
  };
  searchLevelUsed?: string;
  fallbackUsed?: boolean;
  parsedPlace?: string;
  parsedSubject?: string;
  parsedExamFocus?: string;
  interpretationUsed?: string;
  parserConfidence?: number;
  hasMore?: boolean;
  nextPageToken?: string;
  visibleLimit?: number;
  resultScope?: string;
  refinementRequired?: boolean;
  refinementReason?: string;
};

type SearchMode = "natural" | "advanced";

interface ListingMainProps {
  locale: string;
  embedded?: boolean;
  showTitle?: boolean;
}

type ListingCopy = {
  interpretationTitle: string;
  placeLabel: string;
  subjectLabel: string;
  examLabel: string;
  modeLabel: string;
  confidenceLabel: string;
  fallbackNotice: string;
  noResultsTitle: string;
  noResultsGuidance: string;
  noResultsCta: string;
  suggestionPrefillHint: string;
  advancedNoResultsGuidance: string;
  advancedNoResultsHint: string;
  modeMap: Record<string, string>;
};

type RefinementChip = {
  label: string;
  apply: () => void;
};

type PersistedListingState = {
  timestamp: number;
  countryCode: string;
  searchMode: SearchMode;
  naturalQuery: string;
  location: string;
  subject: string;
  text: string;
  items: Centre[];
  searchMeta: SearchResponse | null;
  nextPageToken: string | null;
  hasSearched: boolean;
  lastSearchMode: SearchMode | null;
};

const LISTING_STATE_TTL_MS = 30 * 60 * 1000;
const SEARCH_SLOW_NOTICE_MS = 4000;

const getCountryDisplayName = (countryCode: string, locale: string) => {
  const normalizedCountryCode = normalizeCountryCode(countryCode);
  if (!normalizedCountryCode) {
    return "";
  }

  if (typeof Intl === "undefined" || typeof Intl.DisplayNames === "undefined") {
    return normalizedCountryCode;
  }

  try {
    const displayLocale = locale.split("-")[0] || locale || "en";
    return new Intl.DisplayNames([displayLocale], { type: "region" }).of(normalizedCountryCode) || normalizedCountryCode;
  } catch {
    return normalizedCountryCode;
  }
};

const formatSlugLabel = (value?: string) => {
  if (!value) {
    return "-";
  }

  return value
    .split("-")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
};

const formatBusinessTypeDisplay = (businessType?: string, businessSubtype?: string) => {
  const typeLabel = formatSlugLabel(businessType);
  const subtypeLabel = formatSlugLabel(businessSubtype);

  if (typeLabel === "-" && subtypeLabel === "-") {
    return "-";
  }

  if (typeLabel === "-") {
    return subtypeLabel;
  }

  if (subtypeLabel === "-") {
    return typeLabel;
  }

  return `${typeLabel} - ${subtypeLabel}`;
};

const normalizeText = (value?: string) => (value || "").trim();

const ListingMain: React.FC<ListingMainProps> = ({ locale, embedded = false, showTitle = true }) => {
  const router = useRouter();
  const pathname = usePathname();
  const showSearchInterpretation = process.env.NODE_ENV === "development";
  const searchParams = useSearchParams();
  const hasAutoLoadedFromQuery = useRef(false);
  const hasRestoredPersistedState = useRef(false);
  const { executeRecaptcha } = useReCaptcha();
  const t = useTranslations("EducationListing");
  const appContext = useContext(AppContext) as AppContextType | undefined;
  const currentUser = appContext?.currentUser ?? null;
  const userInfo = appContext?.userInfo;
  const availability = useEducationAvailability();
  const countryCode = availability.countryCode ?? "";
  const isDetectingCountry = availability.status === "loading";
  const isSupported = availability.status === "supported";
  const [searchMode, setSearchMode] = useState<SearchMode>(
    TUTORING_CENTRES_NL_SEARCH_V2_ENABLED ? "natural" : "advanced"
  );
  const [naturalQuery, setNaturalQuery] = useState("");
  const [location, setLocation] = useState("");
  const [subject, setSubject] = useState("");
  const [text, setText] = useState("");
  const [items, setItems] = useState<Centre[]>([]);
  const [searchMeta, setSearchMeta] = useState<SearchResponse | null>(null);
  const [nextPageToken, setNextPageToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSearchTakingLong, setIsSearchTakingLong] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasSearched, setHasSearched] = useState(false);
  const [lastSearchMode, setLastSearchMode] = useState<SearchMode | null>(null);

  const listingStorageKey = useMemo(() => `education-listing-state:${locale}`, [locale]);
  const dashboardLink = useMemo(() => {
    if (!currentUser || !userInfo) {
      return null;
    }

    if (userInfo.activeWorkspace === "tutor" || userInfo.workspaces.includes("tutor")) {
      return {
        href: getLink(locale, Routes.TUTORS_ROOT),
        label: "Back to Tutor dashboard",
      };
    }

    if (userInfo.activeWorkspace === "parent" || userInfo.workspaces.includes("parent")) {
      return {
        href: `/${locale}/parents`,
        label: "Back to Parent dashboard",
      };
    }

    if (userInfo.activeWorkspace === "student" || userInfo.workspaces.includes("student")) {
      return {
        href: `/${locale}/dashboard`,
        label: "Back to Student dashboard",
      };
    }

    return null;
  }, [currentUser, locale, userInfo]);

  const searchReturnTo = useMemo(() => {
    const fallbackPath = `/${locale}/education`;
    if (!pathname) {
      return fallbackPath;
    }

    const query = searchParams.toString();
    return query ? `${pathname}?${query}` : pathname;
  }, [locale, pathname, searchParams]);

  const hasSearchQueryInUrl = useMemo(() => {
    const placeParam = (searchParams.get("place") || "").trim();
    const subjectParam = (searchParams.get("subject") || "").trim();
    const textParam = (searchParams.get("text") || "").trim();
    const queryParam = (searchParams.get("query") || searchParams.get("q") || "").trim();

    return Boolean(placeParam || subjectParam || textParam || queryParam);
  }, [searchParams]);

  const syncSearchQueryToUrl = (state: {
    mode: SearchMode;
    naturalQuery: string;
    location: string;
    subject: string;
    text: string;
  }) => {
    if (!pathname) {
      return;
    }

    const params = new URLSearchParams(searchParams.toString());
    params.delete("mode");
    params.delete("query");
    params.delete("q");
    params.delete("place");
    params.delete("subject");
    params.delete("text");

    params.set("mode", state.mode);

    if (state.mode === "natural") {
      if (state.naturalQuery.trim()) {
        params.set("query", state.naturalQuery.trim());
      }
    } else {
      if (state.location.trim()) {
        params.set("place", state.location.trim());
      }

      if (state.subject.trim()) {
        params.set("subject", state.subject.trim());
      }

      if (state.text.trim()) {
        params.set("text", state.text.trim());
      }
    }

    const nextQuery = params.toString();
    const next = nextQuery ? `${pathname}?${nextQuery}` : pathname;
    router.replace(next, { scroll: false });
  };

  const persistListingState = (state: Omit<PersistedListingState, "timestamp">) => {
    try {
      const payload: PersistedListingState = {
        ...state,
        timestamp: Date.now(),
      };

      sessionStorage.setItem(listingStorageKey, JSON.stringify(payload));
    } catch {
      // Ignore browser storage errors.
    }
  };

  const copy = useMemo<ListingCopy>(
    () => ({
      interpretationTitle: t("InterpretationTitle"),
      placeLabel: t("PlaceLabel"),
      subjectLabel: t("SubjectLabel"),
      examLabel: t("ExamLabel"),
      modeLabel: t("ModeLabel"),
      confidenceLabel: t("ConfidenceLabel"),
      fallbackNotice: t("FallbackNotice"),
      noResultsTitle: t("NoResultsTitle"),
      noResultsGuidance: t("NoResultsGuidance"),
      noResultsCta: t("NoResultsCta"),
      suggestionPrefillHint: t("SuggestionPrefillHint"),
      advancedNoResultsGuidance: t("AdvancedNoResultsGuidance"),
      advancedNoResultsHint: t("AdvancedNoResultsHint"),
      modeMap: {
        pattern_only: t("ModePatternOnly"),
        pattern_plus_classification: t("ModePatternPlusClassification"),
        classification_only: t("ModeClassificationOnly"),
        keyword_fallback: t("ModeKeywordFallback"),
        query_keyword_only: t("ModeQueryKeywordOnly"),
      },
    }),
    [t]
  );

  const formatInterpretationMode = (mode?: string) => {
    if (!mode) {
      return t("ModeNone");
    }

    return copy.modeMap[mode] || mode.replace(/_/g, " ");
  };

  const subjectRefinementChips = useMemo<RefinementChip[]>(() => [
    {
      label: "GCSE",
      apply: () => {
        setSubject("gcse");
      },
    },
    {
      label: "Maths",
      apply: () => {
        setSubject("maths");
      },
    },
  ], []);

  const keywordRefinementChips = useMemo<RefinementChip[]>(() => [
    {
      label: "School",
      apply: () => {
        setText("school");
      },
    },
    {
      label: "Primary School",
      apply: () => {
        setText("primary school");
      },
    },
    {
      label: "Secondary School",
      apply: () => {
        setText("secondary school");
      },
    },
    {
      label: "Tutoring Centre",
      apply: () => {
        setText("tutoring centre");
      },
    },
    {
      label: "Online",
      apply: () => {
        setText("online");
      },
    },
  ], []);

  const extractPlaceFromNaturalQuery = (input: string) => {
    const normalized = input.trim().replace(/\s+/g, " ");
    if (!normalized) {
      return "";
    }

    const lower = normalized.toLowerCase();
    const nearIndex = lower.indexOf(" near ");
    if (nearIndex >= 0) {
      return normalized.slice(nearIndex + 6).trim();
    }

    const inIndex = lower.indexOf(" in ");
    if (inIndex >= 0) {
      return normalized.slice(inIndex + 4).trim();
    }

    return normalized;
  };

  const resolveAdvancedPrefill = () => {
    if (searchMeta?.interpretationUsed === "keyword_fallback" && !searchMeta?.resolvedPlace) {
      return "";
    }

    const source = searchMeta?.parsedPlace?.trim()
      || searchMeta?.resolvedPlace?.name?.trim()
      || searchMeta?.resolvedPlace?.displayName?.trim()
      || extractPlaceFromNaturalQuery(naturalQuery);

    return source || "";
  };

  const mapSearchApiErrorToLocalizedMessage = (rawError: unknown) => {
    const source = (() => {
      if (typeof rawError === "string") {
        return rawError;
      }

      if (rawError && typeof rawError === "object") {
        const record = rawError as Record<string, unknown>;
        if (typeof record.message === "string") {
          return record.message;
        }

        if (typeof record.error === "string") {
          return record.error;
        }

        if (typeof record.title === "string") {
          return record.title;
        }
      }

      return "";
    })();

    const normalized = source.trim().toLowerCase();

    if (normalized.includes("countrycode is required")) {
      return t("ApiErrorCountryCodeRequired");
    }

    if (normalized.includes("place, placeid, or query is required")) {
      return t("ApiErrorPlaceIdOrQueryRequired");
    }

    if (normalized.includes("invalid country/place combination")) {
      return t("ApiErrorInvalidCountryPlaceCombination");
    }

    if (normalized.includes("recaptcha")) {
      return "Search verification failed. Please try again.";
    }

    if (!source.trim()) {
      return t("ErrorFailedToLoadEducationWithPeriod");
    }

    return source;
  };

  const canSearch = useMemo(() => {
    if (!isSupported || countryCode.trim().length === 0) {
      return false;
    }

    if (searchMode === "advanced") {
      return location.trim().length > 0;
    }

    return naturalQuery.trim().length > 0;
  }, [countryCode, isSupported, searchMode, location, naturalQuery]);
  const isRecaptchaReady = Boolean(executeRecaptcha);
  const isSearchDisabled = isLoading || isDetectingCountry || !isRecaptchaReady || !canSearch;

  const countryDisplayName = useMemo(() => getCountryDisplayName(countryCode, locale), [countryCode, locale]);

  const loadCentres = async (override?: {
    mode?: SearchMode;
    naturalQuery?: string;
    location?: string;
    subject?: string;
    text?: string;
    nextPageToken?: string | null;
    append?: boolean;
  }) => {
    if (!isSupported) return;
    const mode = override?.mode ?? searchMode;
    const naturalQueryValue = (override?.naturalQuery ?? naturalQuery).trim();
    const locationValue = (override?.location ?? location).trim();
    const subjectValue = (override?.subject ?? subject).trim();
    const textValue = (override?.text ?? text).trim();
    const appendResults = Boolean(override?.append);
    const overrideNextPageToken = override?.nextPageToken ?? null;
    const parsedNaturalQuery = mode === "natural"
      ? parseNaturalEducationSearchQuery(naturalQueryValue)
      : null;
    const shouldUseStructuredNaturalQuery = Boolean(
      parsedNaturalQuery
      && parsedNaturalQuery.strategy === "structured_subject_place"
      && parsedNaturalQuery.confidence >= 0.8
      && parsedNaturalQuery.inferredPlace
      && parsedNaturalQuery.inferredSubject
    );
    const naturalQueryForRequest = mode === "natural" && !shouldUseStructuredNaturalQuery
      ? naturalQueryValue
      : "";
    const place = mode === "advanced"
      ? locationValue
      : shouldUseStructuredNaturalQuery
        ? parsedNaturalQuery?.inferredPlace?.trim() || ""
        : "";
    const subjectForRequest = mode === "advanced"
      ? subjectValue
      : shouldUseStructuredNaturalQuery
        ? parsedNaturalQuery?.inferredSubject?.trim() || ""
        : "";
    const isValidSearch = countryCode.trim().length > 0
      && (mode === "advanced" ? locationValue.length > 0 : naturalQueryValue.length > 0);

    if (!isValidSearch) {
      setError(mode === "advanced" ? t("ErrorAdvancedRequiresMunicipalityCity") : t("ErrorSearchQueryRequired"));
      return;
    }

    if (mode === "advanced" && !locationValue) {
      setError(t("ErrorProvideMunicipalityCity"));
      return;
    }

    setError(null);
  setIsSearchTakingLong(false);
    setIsLoading(true);
    void trackEducationearchEvent("tc_search_submitted", {
      mode,
      countryCode: countryCode.trim().toUpperCase(),
      queryLength: naturalQueryValue.length,
    });

    try {
      const gRecaptchaToken = await getEducationSearchRecaptchaToken(executeRecaptcha);

      setSearchMeta(null);
      const buildQuery = (targetCountryCode: string) => {
        const query = new URLSearchParams({
          countryCode: targetCountryCode,
          page: "1",
          pageSize: "20",
        });

        if (TUTORING_CENTRES_NL_SEARCH_V2_ENABLED && mode === "natural" && naturalQueryForRequest) {
          query.set("query", naturalQueryForRequest);
        }

        if (place.trim()) {
          query.set("place", place.trim());
        }

        if (subjectForRequest) {
          query.set("subject", subjectForRequest);
        }

        if (mode === "advanced" && textValue) {
          query.set("text", textValue);
        }

        if (overrideNextPageToken) {
          query.set("nextPageToken", overrideNextPageToken);
        }

        return query;
      };

      const executeSearch = async (targetCountryCode: string) => {
        const query = buildQuery(targetCountryCode);
        const idToken = currentUser ? await currentUser.getIdToken() : null;
        const headers: Record<string, string> = {
          "x-recaptcha-token": gRecaptchaToken,
        };

        if (idToken) {
          headers.Authorization = `Bearer ${idToken}`;
        }

        const response = await fetch(`/api/education/search?${query.toString()}`, {
          method: "GET",
          headers,
        });

        const data = await response.json();
        if (!response.ok) {
          throw new Error(mapSearchApiErrorToLocalizedMessage(data?.error ?? data));
        }

        const payload = (data || {}) as SearchResponse;
        const centres = Array.isArray(payload.centres)
          ? payload.centres
          : Array.isArray(data)
            ? (data as Centre[])
            : [];

        return {
          data,
          payload,
          centres,
          countryCodeUsed: targetCountryCode,
        };
      };

      const detectedCountryCode = countryCode.trim().toUpperCase();
      const result = await executeSearch(detectedCountryCode);

      const isCountryKeywordFallback = !Array.isArray(result.data)
        && result.payload.interpretationUsed === "keyword_fallback"
        && !result.payload.resolvedPlace;

      const effectiveItems = isCountryKeywordFallback ? [] : result.centres;
      let nextItems: Centre[] = [];
      setItems((prev) => {
        nextItems = appendResults ? [...prev, ...effectiveItems] : effectiveItems;
        return nextItems;
      });
      setSearchMeta(Array.isArray(result.data) ? null : result.payload);
      setNextPageToken(Array.isArray(result.data) ? null : (result.payload.nextPageToken || null));
      setLastSearchMode(mode);
      setHasSearched(true);

      const nextMeta = Array.isArray(result.data) ? null : result.payload;
      const nextToken = Array.isArray(result.data) ? null : (result.payload.nextPageToken || null);

      syncSearchQueryToUrl({
        mode,
        naturalQuery: naturalQueryValue,
        location: locationValue,
        subject: subjectValue,
        text: textValue,
      });

      persistListingState({
        countryCode: result.countryCodeUsed,
        searchMode: mode,
        naturalQuery: naturalQueryValue,
        location: locationValue,
        subject: subjectValue,
        text: textValue,
        items: nextItems,
        searchMeta: nextMeta,
        nextPageToken: nextToken,
        hasSearched: true,
        lastSearchMode: mode,
      });

      if (result.centres.length === 0) {
        void trackEducationearchEvent("tc_search_no_results", {
          mode,
          interpretationUsed: Array.isArray(result.data) ? "array_legacy" : result.payload.interpretationUsed || "none",
          parserConfidence: Array.isArray(result.data) ? null : result.payload.parserConfidence ?? null,
          fallbackUsed: Array.isArray(result.data) ? false : result.payload.fallbackUsed ?? false,
        });
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : String(err);
      setError(message || t("ErrorFailedToLoadEducationWithPeriod"));
      setItems([]);
      setSearchMeta(null);
      setNextPageToken(null);
      setLastSearchMode(mode);
      setHasSearched(true);
    } finally {
      setIsLoading(false);
    }
  };

  const submitSearch = () => {
    if (isSearchDisabled) {
      return;
    }

    void loadCentres();
  };

  useEffect(() => {
    if (!isLoading) {
      setIsSearchTakingLong(false);
      return;
    }

    const timeoutId = window.setTimeout(() => {
      setIsSearchTakingLong(true);
    }, SEARCH_SLOW_NOTICE_MS);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [isLoading]);

  useEffect(() => {
    if (hasAutoLoadedFromQuery.current || !isSupported || !countryCode.trim() || !executeRecaptcha) {
      return;
    }

    const placeParam = (searchParams.get("place") || "").trim();
    const subjectParam = (searchParams.get("subject") || "").trim();
    const textParam = (searchParams.get("text") || "").trim();
    const queryParam = (searchParams.get("query") || searchParams.get("q") || "").trim();
    const modeParam = (searchParams.get("mode") || "").trim().toLowerCase();

    if (!placeParam && !subjectParam && !textParam && !queryParam) {
      return;
    }

    hasAutoLoadedFromQuery.current = true;

    const shouldUseNatural = TUTORING_CENTRES_NL_SEARCH_V2_ENABLED
      && queryParam.length > 0
      && (modeParam === "natural" || placeParam.length === 0);

    if (shouldUseNatural) {
      setSearchMode("natural");
      setNaturalQuery(queryParam);
      void loadCentres({
        mode: "natural",
        naturalQuery: queryParam,
      });
      return;
    }

    const resolvedPlace = placeParam || queryParam;
    if (!resolvedPlace) {
      return;
    }

    setSearchMode("advanced");
    setLocation(resolvedPlace);
    setSubject(subjectParam);
    setText(textParam);

    void loadCentres({
      mode: "advanced",
      location: resolvedPlace,
      subject: subjectParam,
      text: textParam,
    });
  }, [countryCode, executeRecaptcha, isSupported, searchParams]);

  useEffect(() => {
    if (hasRestoredPersistedState.current || !isSupported || !countryCode.trim() || hasSearchQueryInUrl) {
      return;
    }

    hasRestoredPersistedState.current = true;

    try {
      const raw = sessionStorage.getItem(listingStorageKey);
      if (!raw) {
        return;
      }

      const parsed = JSON.parse(raw) as PersistedListingState;
      if (!parsed || typeof parsed.timestamp !== "number") {
        return;
      }

      if (Date.now() - parsed.timestamp > LISTING_STATE_TTL_MS) {
        sessionStorage.removeItem(listingStorageKey);
        return;
      }

      const persistedCountryCode = normalizeCountryCode(parsed.countryCode);
      if (persistedCountryCode !== countryCode) {
        sessionStorage.removeItem(listingStorageKey);
        return;
      }
      setSearchMode(parsed.searchMode || "advanced");
      setNaturalQuery(parsed.naturalQuery || "");
      setLocation(parsed.location || "");
      setSubject(parsed.subject || "");
      setText(parsed.text || "");
      setItems(Array.isArray(parsed.items) ? parsed.items : []);
      setSearchMeta(parsed.searchMeta || null);
      setNextPageToken(parsed.nextPageToken || null);
      setHasSearched(Boolean(parsed.hasSearched));
      setLastSearchMode(parsed.lastSearchMode || null);

      syncSearchQueryToUrl({
        mode: parsed.searchMode || "advanced",
        naturalQuery: parsed.naturalQuery || "",
        location: parsed.location || "",
        subject: parsed.subject || "",
        text: parsed.text || "",
      });
    } catch {
      // Ignore parse/storage errors.
    }
  }, [countryCode, hasSearchQueryInUrl, isSupported, listingStorageKey]);

  if (!isSupported) {
    return (
      <section className={embedded ? "" : "course__area pt-40 pb-80"}>
        <div className={embedded ? "" : "classBase container"}>
          {!embedded && showTitle && <h1 className="mb-4">{t("Title")}</h1>}
          <AvailabilityNotice availability={availability} />
        </div>
      </section>
    );
  }

  return (
    <section className={embedded ? "" : "course__area pt-40 pb-80"}>
      <div className={embedded ? "" : "classBase container"}>
        {!embedded && showTitle && (
          <div className="mb-4">
            <h1 className="mb-2">{t("Title")}</h1>
          </div>
        )}
        
        <div className="card mb-4 shadow-sm border-0" style={{ borderRadius: 20 }}>
          <div className="card-body">
            <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
              <div className="btn-group" role="group" aria-label={t("SearchModeAriaLabel")}>
                {TUTORING_CENTRES_NL_SEARCH_V2_ENABLED && (
                  <button
                    type="button"
                    className={`btn btn-sm ${searchMode === "natural" ? "btn-primary" : "btn-outline-primary"}`}
                    onClick={() => {
                      setSearchMode("natural");
                      void trackEducationearchEvent("tc_search_mode_toggled", { mode: "natural" });
                    }}
                  >
                    {t("NaturalSearchButton")}
                  </button>
                )}
                <button
                  type="button"
                  className={`btn btn-sm ${searchMode === "advanced" ? "btn-primary" : "btn-outline-primary"}`}
                  onClick={() => {
                    setSearchMode("advanced");
                    void trackEducationearchEvent("tc_search_mode_toggled", { mode: "advanced" });
                  }}
                >
                  {t("AdvancedSearchButton")}
                </button>
              </div>
              <small className="text-muted">
                {
                  // t("CountryLockedHint")
                }
              </small>
            </div>

            <form
              className="row g-3 align-items-end"
              onSubmit={(event) => {
                event.preventDefault();
                submitSearch();
              }}
            >
              <div className="col-md-3">
                <label htmlFor="tc-country-code" className="form-label">{t("CountryLabel")}</label>
                <input
                  id="tc-country-code"
                  className="form-control"
                  value={countryDisplayName}
                  readOnly
                  disabled
                  placeholder={isDetectingCountry ? t("DetectingPlaceholder") : t("NotAvailablePlaceholder")}
                />
                <small className="text-muted">{t("CountryDetectedHint")}</small>
              </div>

              {searchMode === "natural" ? (
                <div className="col-md-9">
                  <label htmlFor="tc-natural-query" className="form-label">{t("SearchQueryLabel")}</label>
                  <input
                    id="tc-natural-query"
                    className="form-control"
                    value={naturalQuery}
                    onChange={(e) => setNaturalQuery(e.target.value)}
                    placeholder={t("SearchQueryPlaceholder")}
                  />
                  <small className="text-muted">{t("SearchQueryHint")}</small>
                </div>
              ) : (
                <>
                  <div className="col-md-9">
                    <label htmlFor="tc-location" className="form-label">{t("LocationLabel")}</label>
                    <input
                      id="tc-location"
                      className="form-control"
                      value={location}
                      onChange={(e) => setLocation(e.target.value)}
                      placeholder={t("LocationPlaceholder")}
                    />
                  </div>
                  <div className="col-md-9">
                    <label htmlFor="tc-subject" className="form-label">{t("SubjectSlugLabel")}</label>
                    <input
                      id="tc-subject"
                      className="form-control"
                      value={subject}
                      onChange={(e) => setSubject(e.target.value)}
                      placeholder={t("SubjectSlugPlaceholder")}
                    />
                    <small className="text-muted d-block mt-2 mb-1">Subject refinements</small>
                    <div className="d-flex flex-wrap gap-2">
                      {subjectRefinementChips.map((chip) => (
                        <button
                          key={chip.label}
                          type="button"
                          className="btn btn-outline-secondary btn-sm"
                          onClick={chip.apply}
                        >
                          {chip.label}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="col-md-9">
                    <label htmlFor="tc-text" className="form-label">{t("KeywordLabel")}</label>
                    <input
                      id="tc-text"
                      className="form-control"
                      value={text}
                      onChange={(e) => setText(e.target.value)}
                      placeholder={t("KeywordPlaceholder")}
                    />
                    <small className="text-muted d-block mt-2 mb-1">Keyword refinements</small>
                    <div className="d-flex flex-wrap gap-2">
                      {keywordRefinementChips.map((chip) => (
                        <button
                          key={chip.label}
                          type="button"
                          className="btn btn-outline-secondary btn-sm"
                          onClick={chip.apply}
                        >
                          {chip.label}
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              )}

              <div className="col-12 d-flex justify-content-end">
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={isSearchDisabled}
                >
                  {isDetectingCountry
                    ? t("DetectingCountryButton")
                    : isLoading
                      ? t("SearchingButton")
                      : t("SearchCentresButton")}
                </button>
              </div>
            </form>
          </div>
        </div>

        {error && <div className="alert alert-danger">{error}</div>}

        {isLoading && (
          <div className="alert alert-info" role="status" aria-live="polite">
            <div className="fw-semibold">
              {isSearchTakingLong ? t("SearchSlowTitle") : t("SearchInProgressTitle")}
            </div>
            <div>
              {isSearchTakingLong ? t("SearchSlowBody") : t("SearchInProgressBody")}
            </div>
          </div>
        )}

        {showSearchInterpretation
          && hasSearched
          && !error
          && searchMeta
          && !searchMeta.resolvedPlace
          && (searchMeta.parsedPlace || searchMeta.parsedSubject || searchMeta.parsedExamFocus || searchMeta.interpretationUsed) && (
          <div className="alert alert-secondary">
            <div className="fw-semibold mb-2">{copy.interpretationTitle}</div>
            <div className="mb-2 d-flex flex-wrap gap-2">
              {searchMeta.parsedPlace && <span className="badge text-bg-light">{copy.placeLabel}: {searchMeta.parsedPlace}</span>}
              {searchMeta.parsedSubject && <span className="badge text-bg-light">{copy.subjectLabel}: {searchMeta.parsedSubject}</span>}
              {searchMeta.parsedExamFocus && <span className="badge text-bg-light">{copy.examLabel}: {searchMeta.parsedExamFocus}</span>}
              {searchMeta.interpretationUsed && <span className="badge text-bg-light">{copy.modeLabel}: {formatInterpretationMode(searchMeta.interpretationUsed)}</span>}
              {typeof searchMeta.parserConfidence === "number" && (
                <span className="badge text-bg-light">{copy.confidenceLabel}: {Math.round(searchMeta.parserConfidence * 100)}%</span>
              )}
            </div>
            <div>{copy.fallbackNotice}</div>
          </div>
        )}

        {showSearchInterpretation && hasSearched && !error && searchMeta?.resolvedPlace && (
          <div className={`alert ${searchMeta.fallbackUsed ? "alert-warning" : "alert-secondary"}`}>
            {(searchMeta.parsedPlace || searchMeta.parsedSubject || searchMeta.parsedExamFocus || searchMeta.interpretationUsed) && (
              <>
                <div className="fw-semibold mb-2">{copy.interpretationTitle}</div>
                <div className="mb-2 d-flex flex-wrap gap-2">
                  {searchMeta.parsedPlace && <span className="badge text-bg-light">{copy.placeLabel}: {searchMeta.parsedPlace}</span>}
                  {searchMeta.parsedSubject && <span className="badge text-bg-light">{copy.subjectLabel}: {searchMeta.parsedSubject}</span>}
                  {searchMeta.parsedExamFocus && <span className="badge text-bg-light">{copy.examLabel}: {searchMeta.parsedExamFocus}</span>}
                  {searchMeta.interpretationUsed && <span className="badge text-bg-light">{copy.modeLabel}: {formatInterpretationMode(searchMeta.interpretationUsed)}</span>}
                  {typeof searchMeta.parserConfidence === "number" && (
                    <span className="badge text-bg-light">{copy.confidenceLabel}: {Math.round(searchMeta.parserConfidence * 100)}%</span>
                  )}
                </div>
              </>
            )}
            <div>
              <strong>{t("ResolvedPlaceLabel")}</strong> {searchMeta.resolvedPlace.displayName || searchMeta.resolvedPlace.name || searchMeta.resolvedPlace.id}
            </div>
            <div>
              <strong>{t("SearchLevelUsedLabel")}</strong> {searchMeta.searchLevelUsed || searchMeta.resolvedPlace.level || t("UnknownValue")}
            </div>
            {searchMeta.fallbackUsed && <div>{t("ParentFallbackNotice")}</div>}
          </div>
        )}

        {hasSearched && !error && items.length === 0 && !searchMeta?.refinementRequired && (
          <div className="alert alert-info">
            <div>{copy.noResultsTitle}</div>
            {TUTORING_CENTRES_NL_SEARCH_V2_ENABLED && lastSearchMode === "natural" ? (
              <div className="mt-2 d-flex align-items-center justify-content-between flex-wrap gap-2">
                <div>
                  <div>{copy.noResultsGuidance}</div>
                  <small className="text-muted">{copy.suggestionPrefillHint}</small>
                </div>
                <button
                  type="button"
                  className="btn btn-outline-primary btn-sm"
                  onClick={() => {
                    setSearchMode("advanced");
                    void trackEducationearchEvent("tc_search_advanced_suggested_clicked", {
                      previousMode: "natural",
                    });
                    const suggested = resolveAdvancedPrefill();
                    if (!location.trim() && suggested) {
                      setLocation(suggested);
                    }
                  }}
                >
                  {copy.noResultsCta}
                </button>
              </div>
            ) : (
              <div className="mt-2">
                <div>{copy.advancedNoResultsGuidance}</div>
                {TUTORING_CENTRES_NL_SEARCH_V2_ENABLED && <small className="text-muted">{copy.advancedNoResultsHint}</small>}
              </div>
            )}
          </div>
        )}

        {hasSearched && !error && searchMeta?.refinementRequired && (
          <div className="alert alert-warning">
            <div className="fw-semibold mb-1">Refine your search</div>
            <div>
              {searchMeta.refinementReason
                || "Too many results for this area. Add a subject, provider type, age range, delivery mode, or a more specific locality."}
            </div>
            <div className="mt-3">
              <button
                type="button"
                className="btn btn-outline-primary btn-sm"
                onClick={() => {
                  setSearchMode("advanced");
                  if (!location.trim()) {
                    const suggested = resolveAdvancedPrefill();
                    if (suggested) {
                      setLocation(suggested);
                    }
                  }
                }}
              >
                Open advanced search
              </button>
            </div>
          </div>
        )}

        {hasSearched && !error && searchMeta?.hasMore && !searchMeta?.refinementRequired && (
          <div className="alert alert-secondary">
            <div>
              Showing the first {items.length} results
              {typeof searchMeta.visibleLimit === "number" ? ` (up to ${searchMeta.visibleLimit} visible for this search scope).` : "."}
            </div>
            <small className="text-muted">Refine by subject, type, age range, or locality for more precise matches.</small>
            {lastSearchMode === "natural" && (
              <div className="mt-3">
                <button
                  type="button"
                  className="btn btn-outline-primary btn-sm"
                  onClick={() => {
                    setSearchMode("advanced");
                    const suggested = resolveAdvancedPrefill();
                    if (!location.trim() && suggested) {
                      setLocation(suggested);
                    }
                  }}
                >
                  Open advanced search
                </button>
              </div>
            )}
          </div>
        )}

        <div className="row g-3">
          {items.map((centre) => {
            const slug = centre.slug || centre.id;
            const countryName = centre.countryCode ? getCountryDisplayName(centre.countryCode, locale) : "";
            const businessName = normalizeText(centre.businessGroupName) || normalizeText(centre.name) || t("UnnamedCentre");
            const campusName = normalizeText(centre.campusName);
            const locationLine = [centre.localityName || centre.city, centre.adminLevel2Name || centre.municipality, centre.adminLevel1Name, countryName]
              .filter(Boolean)
              .join(" • ") || t("LocationNotProvided");
            const phoneValue = (centre.phone || "-").trim() || "-";
            const businessTypeDisplay = formatBusinessTypeDisplay(centre.businessTypeSlug, centre.businessSubtypeSlug);

            return (
              <div className="col-md-6" key={centre.id}>
                <article
                  className="card h-100 border-0 overflow-hidden"
                  style={{ borderRadius: 20, boxShadow: "0 12px 30px rgba(19, 58, 92, 0.08)" }}
                >
                  <div style={{ height: 4, background: "linear-gradient(90deg, #1e8a67 0%, #2e6fd4 100%)" }} />
                  <div className="card-body d-flex flex-column">
                    <div className="d-flex flex-wrap align-items-start justify-content-between gap-2 mb-3">
                      <h4 className="mb-0" style={{ color: "#123a5c" }}>{businessName}</h4>
                      <span className="badge text-bg-light">{countryName || "-"}</span>
                    </div>

                    {campusName && (
                      <p className="mb-2 text-muted" style={{ fontSize: "0.9rem" }}>
                        {campusName} {t("CampusLabelSuffix")}
                        {centre.isPrimaryCampus ? ` • ${t("PrimaryCampusSuffix")}` : ""}
                      </p>
                    )}

                    <p className="mb-3 text-muted" style={{ fontSize: "0.95rem" }}>
                      {locationLine}
                    </p>

                    <div className="p-3 mb-3" style={{ borderRadius: 14, background: "#f7fafc", border: "1px solid #e6eef5" }}>
                      <div className="row g-2">
                        <div className="col-sm-6">
                          <small className="text-muted d-block">Phone</small>
                          <span style={{ color: "#173a50", fontWeight: 500 }}>{phoneValue}</span>
                        </div>
                        <div className="col-sm-6">
                          <small className="text-muted d-block">Type</small>
                          <span style={{ color: "#173a50", fontWeight: 500 }}>{businessTypeDisplay}</span>
                        </div>
                      </div>
                    </div>

                    <div className="d-flex justify-content-end align-items-center gap-2 flex-wrap">
                      <Link
                        href={`/${locale}/education/${encodeURIComponent(slug)}?returnTo=${encodeURIComponent(searchReturnTo)}`}
                        className="btn btn-outline-primary btn-sm"
                      >
                        {t("ViewProfileButton")}
                      </Link>
                    </div>
                  </div>
                </article>
              </div>
            );
          })}
        </div>

        {hasSearched && !error && !searchMeta?.refinementRequired && searchMeta?.hasMore && nextPageToken && items.length > 0 && (
          <div className="d-flex justify-content-center mt-4">
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={() => {
                void loadCentres({
                  mode: searchMode,
                  nextPageToken,
                  append: true,
                });
              }}
              disabled={isLoading}
            >
              {isLoading ? "Loading..." : "Load more results"}
            </button>
          </div>
        )}
      </div>
    </section>
  );
};

export default ListingMain;
